import {
  type InfiniteData,
  infiniteQueryOptions,
  type QueryKey,
  queryOptions,
  skipToken,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { isNotNil, last, sortBy } from 'rambda';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';
import type { z } from 'zod';
import { friendsQueryOptions } from '@/hooks/contacts/use-friends';
import { groupsQueryOptions } from '@/hooks/contacts/use-groups';
import { generateDeletedMessagesQueryOptions } from '@/hooks/conversations/use-deleted-messages';
import useMarkSessionRead from '@/hooks/conversations/use-mark-session-read';
import { generateGroupInfoQueryOptions } from '@/hooks/groups/use-group-info';
import { allChatSessionQueryOptions } from '@/hooks/sessions/use-conversation-sessions';
import { generateFriendInfoQueryOptions } from '@/hooks/users/use-friend-info';
import { type ConversationMessageTypeUnion, conversationMessageUnionScheme } from '@/models/conversations/conversation';
import type { ConversationType } from '@/models/conversations/conversation-session';
import { generateListResponseScheme, type ListRespondedWith } from '@/models/response';
import ROUTES from '@/routes';
import { messageManager } from '@/services/message-manager';
import { promiseLogger } from '@/services/sentry';
import { useConversationSessionStore } from '@/stores/conversation-session';
import { addMessages, checkMissingMessages, useMessageStore } from '@/stores/message';
import { usePeriodicMessageStore } from '@/stores/periodic-message';
import { addToast } from '@/stores/toast';
import { isDeleted } from '@/utils/check-messages-status';
import { fetcher, wait } from '@/utils/fetcher';
import { transformMessage } from '@/utils/message';
import { timeoutPromise } from '@/utils/promise';
import { generateSessionKey } from '@/utils/session';
import { reportStorageError } from '@/utils/storage-error';

type ChatHistoryDirection = 'next' | 'prev';

interface MessageHistoryHookParams {
  type: ConversationType;
  id: Nullable<number>;
  anchorMessageId?: number;
  direction: ChatHistoryDirection;
}

type MessageHistoryInfiniteQueryPageParams = Required<Pick<MessageHistoryHookParams, 'anchorMessageId' | 'direction'>>;

type FetchMessagesParams = Omit<MessageHistoryHookParams, 'anchorMessageId'> &
  Required<Pick<MessageHistoryHookParams, 'anchorMessageId'>> & {
    networkOnly?: boolean;
    status?: 0 | 1 | 2;
  };

/**
 * Fetches a list of messages from the database based on the provided parameters.
 * The function retrieves messages for a specific chat session or group,
 * supports pagination in both directions, and handles missing or invalid data explicitly.
 *
 * @function
 * @param {MessageHistoryHookParams} params - Parameters required to retrieve messages.
 * @param {('session'|'group')} params.type - The type of chat context ('session' for individual chat, 'group' for group chat).
 * @param {number} [params.id] - The identifier for the chat session or group.
 * @param {number} [params.anchorMessageId] - The message ID used as a reference point for pagination.
 * @param {('prev'|'next')} params.direction - The direction for message pagination ('prev' for older messages, 'next' for newer messages).
 * @returns {Promise<ConversationMessageTypeUnion[]>} Resolves to an array of messages, sorted by their ID. Returns an empty array if `id` is invalid or if there are missing messages.
 */
async function fetchMessagesFromDb({
  type,
  id,
  direction,
  anchorMessageId = Number.MAX_SAFE_INTEGER,
}: MessageHistoryHookParams): Promise<ConversationMessageTypeUnion[]> {
  if (!id) return [];

  const messages = await timeoutPromise(
    () =>
      promiseLogger({
        name: 'MESSAGES',
        requestAttributes: {
          source: 'indexeddb',
          type,
          id,
          count: anchorMessageId >= Number.MAX_SAFE_INTEGER ? 200 : 50,
          direction,
          anchorMessageId,
        },
        action: () =>
          messageManager.dispatch(
            'getMessages',
            type,
            id,
            anchorMessageId >= Number.MAX_SAFE_INTEGER ? 200 : 50,
            direction,
            anchorMessageId,
          ),
      }).catch((error): ConversationMessageTypeUnion[] => {
        reportStorageError(error, 'useMessageHistory', 'fetchMessagesFromDb', 'dexie');
        return [];
      }),
    3_000,
    (): ConversationMessageTypeUnion[] => [],
  );

  if (messages.every(isDeleted)) return [];

  const missingMessageIds = checkMissingMessages(messages);
  if (missingMessageIds.size > 0) return [];

  return sortBy((m) => m.ID, messages);
}

/**
 * Fetches a set of messages based on the specified parameters.
 *
 * This function is an asynchronous operation that retrieves message data either from a local database or a remote server,
 * depending on the provided options. It supports pagination in both forward and backward directions, and allows customization
 * of the source and status of the messages being fetched.
 *
 * The function will first attempt to fetch messages from a local database (only if `networkOnly` is false), and if a sufficient number
 * of messages is available locally, it returns those messages. Otherwise, it fetches the messages remotely via an API call.
 *
 * If no messages are found when fetching remotely in the "prev" direction with a "status" of 2,
 * or the retrieved messages should have been deleted, the function retries the API request with a "status" of 1.
 *
 * The returned results are sorted based on the message ID, and additional metadata about the fetched messages is included in
 * the return value.
 *
 * @param {FetchMessagesParams} params - The parameters for the message fetch operation.
 * @param {('session'|'group')} params.type - The type of the chat session, such as "session" or "group".
 * @param {number} [params.id] - The unique identifier of the chat session or group.
 * @param {string} params.anchorMessageId - The ID of the anchor message used for pagination.
 * @param {('prev'|'next')} params.direction - The direction for pagination, either "prev" (previous messages) or "next" (future messages).
 * @param {boolean} [params.networkOnly=false] - Indicates whether to fetch messages exclusively from the network.
 * @param {(0|1|2)} [params.status=2] - The message status to filter by during the fetch operation.
 *   - `0`: return only deleted messages
 *   - `1`: return only valid messages
 *   - `2`: return all messages without filtration
 * @param insertion - Insert response into indexedDB
 * @returns {Promise<MessageHistoryResponseType>} A Promise that resolves to the response containing the message history and metadata.
 */
export async function fetchMessages(
  { type, id, anchorMessageId, direction, networkOnly = false, status = 2 }: FetchMessagesParams,
  insertion = true,
): Promise<MessageHistoryResponseType> {
  if (!networkOnly) {
    const storedMessages = await fetchMessagesFromDb({
      type,
      id,
      anchorMessageId,
      direction,
    });

    if (storedMessages.length >= 50) {
      await addMessages(storedMessages).catch(console.warn);

      return {
        Code: 0,
        Msg: '',
        Data: storedMessages,
        Direction: direction,
        AnchorMessageId: anchorMessageId,
        Count: storedMessages.length,
      };
    }
  }

  const response: ListRespondedWith<ConversationMessageTypeUnion> = await fetcher(
    type === 'session' ? '/GetChatData' : '/GetGroupChatData',
    {
      TimeStamp: Date.now(),
      Status: status,
      [type === 'session' ? 'ChatSessionID' : 'GroupID']: id,
      [direction === 'prev' ? 'UnReadMaxID' : 'FromID']: anchorMessageId,
    },
    {
      schema: messageHistoryResponseScheme,
    },
  );

  if (direction === 'prev' && status === 2 && response.Data?.every(isDeleted)) {
    const onlyValidMessageResponse = await fetchMessages({
      type,
      id,
      anchorMessageId,
      direction,
      networkOnly: true,
      status: 1,
    });

    if (onlyValidMessageResponse.Data) {
      response.Data = response.Data.concat(onlyValidMessageResponse.Data);
    }
  }

  const messages = (response.Data ?? []).map(transformMessage);

  if (insertion) await addMessages(messages).catch(console.warn);

  return {
    ...response,
    Data: messages.length ? sortBy((m) => m.ID, messages) : undefined,
    Direction: direction,
    AnchorMessageId: anchorMessageId,
    Count: messages.length ?? 0,
  };
}

export const generateMessageHistoryQueryKey = (type: ConversationType, id: Nullable<number>, ...extras: unknown[]) =>
  [type === 'session' ? '/GetChatData' : '/GetGroupChatData', id, ...extras].filter(isNotNil) as QueryKey;

export const generateMessageHistoryQueryOptions = ({
  type,
  id,
  anchorMessageId = Number.MAX_SAFE_INTEGER,
}: MessageHistoryHookParams) =>
  queryOptions({
    enabled: Boolean(id && anchorMessageId),
    queryKey: generateMessageHistoryQueryKey(type, id, anchorMessageId),
    queryFn:
      id && !anchorMessageId
        ? () =>
            fetchMessages({
              type,
              id,
              anchorMessageId,
              direction: 'prev',
              networkOnly: true,
            })
        : skipToken,
    staleTime: 0,
    gcTime: 0,
    refetchOnReconnect: 'always',
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });

export const generateMessageHistoryInfiniteQueryOptions = ({
  type,
  id,
  anchorMessageId,
}: Omit<MessageHistoryHookParams, 'direction'>) =>
  infiniteQueryOptions<
    MessageHistoryResponseType,
    void,
    InfiniteData<MessageHistoryResponseType>,
    QueryKey,
    MessageHistoryInfiniteQueryPageParams
  >({
    enabled: Boolean(id),
    queryKey: generateMessageHistoryQueryKey(type, id, 'infinite', anchorMessageId),
    queryFn: id
      ? async ({ pageParam: { anchorMessageId, direction } }) => {
          const res = await fetchMessages({
            type,
            id,
            anchorMessageId,
            direction,
          });
          // fetch more data to make virtual message list scrollable
          if (res.Data?.length && res.Data.length < 20) {
            const [minId] = getMinMaxMessageIds(res.Data);
            const moreRes = await fetchMessages({
              type,
              id,
              anchorMessageId: minId,
              direction: direction === 'prev' ? 'next' : 'prev',
            });

            const combined = sortBy((m) => m.ID, [...res.Data, ...(moreRes.Data ?? [])]);

            return {
              ...res,
              Data: combined,
              Count: combined.length,
            };
          }

          return res;
        }
      : skipToken,
    initialPageParam: anchorMessageId
      ? {
          anchorMessageId: anchorMessageId - 1,
          direction: 'next',
        }
      : {
          anchorMessageId: Number.MAX_SAFE_INTEGER,
          direction: 'prev',
        },
    getNextPageParam: (lastPage, _, __, allPageParams) => {
      if (!hasMoreMessage(lastPage)) return null;

      const [, anchorMessageId] = getMinMaxMessageIds(lastPage.Data);

      if (
        allPageParams.filter((params) => params.direction === 'next' && params.anchorMessageId === anchorMessageId)
          .length >= 2
      )
        return null;

      return {
        direction: 'next',
        anchorMessageId,
      };
    },
    getPreviousPageParam: (firstPage, _, __, allPageParams) => {
      if (!hasMoreMessage(firstPage) && firstPage.Direction === 'prev') return null;

      const purgedMessageId = usePeriodicMessageStore.getState().purgedMessageIds[generateSessionKey(type, id)];
      if (purgedMessageId && firstPage.Data?.some((m) => m.ID <= purgedMessageId)) return null;

      const [anchorMessageId] = getMinMaxMessageIds(firstPage.Data);

      if (
        allPageParams.filter((params) => params.direction === 'prev' && params.anchorMessageId === anchorMessageId)
          .length >= 2
      )
        return null;

      return {
        direction: 'prev',
        anchorMessageId,
      };
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

export const generateMessageMissingHistoryQueryOptions = ({
  type,
  id,
  missingMessageIds,
}: Pick<MessageHistoryHookParams, 'type' | 'id'> & {
  missingMessageIds: Set<number>;
}) =>
  queryOptions({
    enabled: Boolean(id) && missingMessageIds.size > 0,
    queryKey: generateMessageHistoryQueryKey(type, id, Array.from(missingMessageIds.keys())),
    queryFn: id
      ? async () => {
          let missingIds = Array.from(missingMessageIds.values());
          const retrievedMessages = [];

          while (missingIds.length > 0) {
            const firstMessageId = missingIds.shift();
            if (!firstMessageId) break;

            const response = await fetchMessages(
              {
                type,
                id,
                anchorMessageId: firstMessageId,
                direction: 'prev',
                networkOnly: true,
              },
              false,
            );

            if (response.Data) {
              const messages = response.Data;
              const messageIds = messages.map((m) => m.ID);
              missingIds = missingIds.filter((id) => !messageIds.includes(id));
              retrievedMessages.push(...messages);
              await wait(600);
            }
          }

          await addMessages(retrievedMessages);

          return retrievedMessages;
        }
      : skipToken,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    throwOnError: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

export default function useMessageHistory(
  type: ConversationType,
  id: number | null | undefined,
  initialMessageId?: number,
  defaultEnabled = true,
) {
  const [messageId, setMessageId] = useState(initialMessageId);
  const [isEnabled, setEnable] = useState(() => defaultEnabled);
  const { missingMessageIds } = useMessageStore();
  const { markAllRead } = useMarkSessionRead();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const infiniteQueryOptions = generateMessageHistoryInfiniteQueryOptions({
    type,
    id,
    anchorMessageId: messageId,
  });

  const {
    data,
    isLoading,
    isSuccess,
    error,
    fetchNextPage,
    fetchPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    hasNextPage,
    hasPreviousPage,
  } = useInfiniteQuery({
    enabled: isEnabled,
    ...infiniteQueryOptions,
    select: ({ pages, pageParams }) => ({
      pages: pages.map((p) => p.Data),
      pageParams,
      error: pages.findLast((p) => [11, 21].includes(p.Code))?.Msg,
    }),
  });

  useQuery(generateMessageHistoryQueryOptions({ type, id, direction: 'prev', anchorMessageId: messageId }));
  useQuery(generateMessageMissingHistoryQueryOptions({ type, id, missingMessageIds }));
  useQuery(generateDeletedMessagesQueryOptions(type, id));

  const debouncedMarkAllRead = useDebouncedCallback(markAllRead, 600);

  useEffect(() => {
    if (data?.error) {
      addToast({
        type: 'warning',
        message: data.error,
      });
      if (id) {
        // 1. remove message store
        useMessageStore.getState().deleteBySessionId(type, id).catch(console.error);
        // 2. remove entry in sessions
        const key = generateSessionKey(type, id);
        const session = useConversationSessionStore.getState().removeSession(key);
        // 3. remove friend/group
        Promise.allSettled([
          queryClient.invalidateQueries(allChatSessionQueryOptions),
          ...(type === 'session'
            ? [
                queryClient.invalidateQueries(friendsQueryOptions),
                queryClient.invalidateQueries(generateFriendInfoQueryOptions(session?.UserID)),
              ]
            : [
                queryClient.invalidateQueries(groupsQueryOptions),
                queryClient.invalidateQueries(generateGroupInfoQueryOptions(id)),
              ]),
        ]).catch(console.warn);
        // 4. nav to session list page
        navigate(ROUTES.MESSAGES.$path(), { replace: true });
      }
    }
  }, [type, id, data?.error]);

  useEffect(() => {
    if (isSuccess && id) {
      const maxReadId =
        last(data?.pages)?.reduce((max, curr) => (curr.Status === null || max > curr.ID ? max : curr.ID), 0) ?? 0;
      debouncedMarkAllRead(type, id, maxReadId, 'message-history-success');
    }
  }, [isSuccess, type, id, data?.pages]);

  const resetInfiniteQuery = (messageId?: number) => {
    setEnable(false);
    setMessageId(messageId);
    setTimeout(() => setEnable(true), 2_000);
  };

  return {
    data,
    fetchNextPage,
    fetchPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isLoading,
    error,
    isEnabled,
    setEnable,
    resetInfiniteQuery,
    setMessageId,
  };
}

const messageHistoryResponseScheme = generateListResponseScheme(conversationMessageUnionScheme);

type MessageHistoryResponseType = ListRespondedWith<z.infer<typeof conversationMessageUnionScheme>> & {
  Direction: ChatHistoryDirection;
  AnchorMessageId: number;
  Count: number;
};

function hasMoreMessage(response: MessageHistoryResponseType) {
  if (!response.Data) return false;
  return (response.Code === -1 || response.Code === 0) && response.Count >= 50;
}

function getMinMaxMessageIds(
  input: Nullable<ConversationMessageTypeUnion[] | Record<number, ConversationMessageTypeUnion>>,
): [number, number] {
  if (!input) return [0, Number.MAX_SAFE_INTEGER];

  const messages = Array.isArray(input) ? input : Object.values(input);
  const ids = messages.map((m) => m.ID);

  return [Math.min(...ids), Math.max(...ids)] as const;
}
